import React, { useCallback, useEffect, useRef, useState } from "react"
import {
    MdFormatBold,
    MdFormatItalic,
    MdFormatUnderlined,
    MdInsertLink,
    MdFormatListBulleted,
    MdFormatListNumbered,
} from "react-icons/md"
import styles from "./richTextEditor.module.scss"

export interface RichTextEditorProps {
    /** Current value as an HTML string. */
    value: string
    /** Fires with the editor's HTML on every edit. */
    onChange: (html: string) => void
    placeholder?: string
    /** Max height (px) of the editable area before it scrolls. Default 220. */
    maxHeightPx?: number
    disabled?: boolean
}

type ToolbarCommand =
    | { kind: "command"; command: "bold" | "italic" | "underline" | "insertUnorderedList" | "insertOrderedList"; icon: React.ReactNode; title: string }
    | { kind: "link"; icon: React.ReactNode; title: string }

const TOOLBAR: ToolbarCommand[] = [
    { kind: "command", command: "bold", icon: <MdFormatBold size={18} />, title: "Bold" },
    { kind: "command", command: "italic", icon: <MdFormatItalic size={18} />, title: "Italic" },
    { kind: "command", command: "underline", icon: <MdFormatUnderlined size={18} />, title: "Underline" },
    { kind: "link", icon: <MdInsertLink size={18} />, title: "Insert link" },
    { kind: "command", command: "insertUnorderedList", icon: <MdFormatListBulleted size={18} />, title: "Bullet list" },
    { kind: "command", command: "insertOrderedList", icon: <MdFormatListNumbered size={18} />, title: "Numbered list" },
]

/** True when the HTML has no visible text/content (ignoring empty tags & whitespace). */
export const isHtmlEmpty = (html: string | null | undefined): boolean => {
    if (!html) return true
    const stripped = html
        .replace(/<br\s*\/?>/gi, "")
        .replace(/&nbsp;/gi, "")
        .replace(/<[^>]*>/g, "")
        .trim()
    return stripped.length === 0
}

/** Escape HTML special chars so pasted plain text can't inject markup. */
const escapeHtml = (text: string): string =>
    text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;")

// Matches http(s):// URLs and bare www. URLs.
const URL_REGEX = /((?:https?:\/\/|www\.)[^\s<]+[^\s<.,;:!?)"'\]])/gi

/** True when the entire string is a single URL (used for "wrap selection" behavior). */
const isSingleUrl = (text: string): boolean => {
    const t = text.trim()
    if (!t || /\s/.test(t)) return false
    const m = t.match(URL_REGEX)
    return Boolean(m && m.length === 1 && m[0] === t)
}

/** Normalize a matched URL into a valid href (prepend https:// for bare www. links). */
const toHref = (url: string): string => (/^https?:\/\//i.test(url) ? url : `https://${url}`)

/** Escape plain text and turn any URLs into anchor tags; keep line breaks as <br>. */
const linkifyToHtml = (text: string): string =>
    text
        .split(/\r\n|\r|\n/)
        .map((line) =>
            escapeHtml(line).replace(URL_REGEX, (raw) => {
                // `raw` came from escaped text, so href is rebuilt from the escaped form (safe).
                const href = toHref(raw)
                return `<a href="${href}" target="_blank" rel="noopener noreferrer">${raw}</a>`
            }),
        )
        .join("<br>")

/**
 * Lightweight, dependency-free rich-text editor built on a `contentEditable` div +
 * `document.execCommand`. Emits an HTML string. Supports bold / italic / underline /
 * link / bullet list / numbered list. The editable area is height-capped and scrolls.
 *
 * Controlled-but-uncontrolled pattern: we only push `value` into the DOM when it differs
 * from what's already rendered, so typing never resets the caret.
 */
const RichTextEditor: React.FC<RichTextEditorProps> = ({
    value,
    onChange,
    placeholder = "Write the itinerary…",
    maxHeightPx = 220,
    disabled = false,
}) => {
    const editorRef = useRef<HTMLDivElement>(null)
    const [activeStates, setActiveStates] = useState<Record<string, boolean>>({})

    // Sync external value → DOM only when it actually differs (avoids caret jumps while typing).
    useEffect(() => {
        const el = editorRef.current
        if (!el) return
        const incoming = value || ""
        if (el.innerHTML !== incoming) {
            el.innerHTML = incoming
        }
    }, [value])

    const emitChange = useCallback(() => {
        const el = editorRef.current
        if (!el) return
        const html = el.innerHTML
        onChange(isHtmlEmpty(html) ? "" : html)
    }, [onChange])

    // Refresh toolbar active highlighting from the current selection.
    const refreshActiveStates = useCallback(() => {
        if (disabled) return
        const next: Record<string, boolean> = {}
        ;["bold", "italic", "underline", "insertUnorderedList", "insertOrderedList"].forEach((cmd) => {
            try {
                next[cmd] = document.queryCommandState(cmd)
            } catch {
                next[cmd] = false
            }
        })
        setActiveStates(next)
    }, [disabled])

    const runCommand = useCallback(
        (command: string, arg?: string) => {
            if (disabled) return
            editorRef.current?.focus()
            document.execCommand(command, false, arg)
            emitChange()
            refreshActiveStates()
        },
        [disabled, emitChange, refreshActiveStates],
    )

    const handleLink = useCallback(() => {
        if (disabled) return
        const url = window.prompt("Enter URL (include https://)", "https://")
        if (!url) return
        const trimmed = url.trim()
        if (!trimmed) return
        runCommand("createLink", trimmed)
    }, [disabled, runCommand])

    // Auto-detect URLs on paste and insert them as clickable links.
    // - Pasting a single URL onto a text selection → wrap the selection as a link.
    // - Otherwise → insert the pasted plain text with any URLs linkified.
    // We always take the plain-text form of the clipboard (ignoring source HTML) so the
    // result is clean and safe; the inserted markup is built from escaped text.
    const handlePaste = useCallback(
        (e: React.ClipboardEvent<HTMLDivElement>) => {
            if (disabled) return
            const text = e.clipboardData.getData("text/plain")
            if (!text) return
            e.preventDefault()

            const selection = window.getSelection()
            const hasSelection = Boolean(selection && selection.toString().length > 0)

            if (hasSelection && isSingleUrl(text)) {
                // Turn the highlighted text into a link pointing at the pasted URL.
                document.execCommand("createLink", false, toHref(text.trim()))
            } else {
                document.execCommand("insertHTML", false, linkifyToHtml(text))
            }
            emitChange()
            refreshActiveStates()
        },
        [disabled, emitChange, refreshActiveStates],
    )

    return (
        <div className={`${styles.wrapper} ${disabled ? styles.disabled : ""}`}>
            <div className={styles.toolbar} role="toolbar" aria-label="Text formatting">
                {TOOLBAR.map((tool, idx) => {
                    const isActive = tool.kind === "command" && activeStates[tool.command]
                    return (
                        <button
                            key={idx}
                            type="button"
                            className={`${styles.toolBtn} ${isActive ? styles.toolBtnActive : ""}`}
                            title={tool.title}
                            aria-label={tool.title}
                            aria-pressed={Boolean(isActive)}
                            disabled={disabled}
                            // onMouseDown preventDefault keeps the editor selection intact when clicking a button.
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => (tool.kind === "link" ? handleLink() : runCommand(tool.command))}
                        >
                            {tool.icon}
                        </button>
                    )
                })}
            </div>
            <div
                ref={editorRef}
                className={styles.editor}
                style={{ maxHeight: maxHeightPx }}
                contentEditable={!disabled}
                suppressContentEditableWarning
                data-placeholder={placeholder}
                onInput={emitChange}
                onPaste={handlePaste}
                onKeyUp={refreshActiveStates}
                onMouseUp={refreshActiveStates}
                onFocus={refreshActiveStates}
            />
        </div>
    )
}

export default RichTextEditor
